import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../store/auth';
import PlanView from '../components/PlanView';

const SEM_ALL = Array.from({length:20}, (_,i) => `S${i+1}`);

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState('sites');
  return (
    <div style={st.page}>
      <div style={st.header} className="lw-band">
        <div className="lw-head-inner">
        <div>
          <h1 style={st.title}>Administration</h1>
          <p style={st.sub}>LIDWORK | {user?.name}</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button style={st.hBtn} onClick={() => nav('/dashboard')}>Tableau de bord</button>
          <button style={st.hBtn} onClick={logout}>Déconnexion</button>
        </div>
        </div>
      </div>
      <div style={st.tabs}>
        <div className="lw-tabs-inner">
        {[['sites','Chantiers'],['users','Utilisateurs']].map(([t,l]) => (
          <button key={t} style={{...st.tab, ...(tab===t?st.tabOn:{})}} onClick={()=>setTab(t)}>{l}</button>
        ))}
        </div>
      </div>
      <div className="lw-shell">
        {tab === 'sites' ? <SitesTab/> : <UsersTab/>}
      </div>
    </div>
  );
}

// ────────────────────────── CHANTIERS ──────────────────────────
function SitesTab() {
  const [sites, setSites] = useState([]);
  const [sel, setSel] = useState(null);       // site en édition
  const [creating, setCreating] = useState(false);
  const load = () => api.get('/sites').then(r => setSites(r.data));
  useEffect(() => { load(); }, []);

  if (sel || creating) return (
    <SiteForm site={sel} onDone={() => { setSel(null); setCreating(false); load(); }}/>
  );

  return (
    <div>
      <button style={{...st.btn, marginBottom:14}} onClick={() => setCreating(true)}>+ Nouveau chantier</button>
      {sites.map(s => (
        <div key={s.id} style={st.rowCard} onClick={() => setSel(s)}>
          <div>
            <span style={{ fontWeight:700, color:'#1E3A6E' }}>{s.nom}</span>
            <p style={{ fontSize:12, color:'#888' }}>
              {[s.client, s.ville].filter(Boolean).join(' – ')} · {s._count?.accessPoints ?? 0} AP · {s._count?.reports ?? 0} rapports
            </p>
          </div>
          <span style={{...st.badge, background: s.statut==='ACTIF'?'#e8f5e9':'#eee',
            color: s.statut==='ACTIF'?'#2E7D32':'#888'}}>{s.statut}</span>
        </div>
      ))}
    </div>
  );
}

function SiteForm({ site, onDone }) {
  const isNew = !site;
  const [f, setF] = useState(site ? { ...site } : { nom:'', client:'', adresse:'', ville:'',
    statut:'ACTIF', semaines:['S1'], notes:'' });
  const [aps, setAps] = useState([]);
  const [users, setUsers] = useState([]);
  const [assigned, setAssigned] = useState(new Set());
  const [mats, setMats] = useState([]);
  const [importText, setImportText] = useState('');
  const [msg, setMsg] = useState('');
  const [sub, setSub] = useState('infos'); // infos | ap | materiaux | equipe

  useEffect(() => {
    if (isNew) return;
    api.get(`/sites/${site.id}/ap`).then(r => setAps(r.data));
    api.get(`/sites/${site.id}/materiaux`).then(r => setMats(r.data));
    api.get(`/sites/${site.id}`).then(r => setAssigned(new Set(r.data.users.map(u => u.user.id))));
    api.get('/users').then(r => setUsers(r.data));
    // eslint-disable-next-line
  }, []);

  const upd = p => setF(x => ({ ...x, ...p }));
  const toggleSem = s => upd({ semaines: f.semaines.includes(s)
    ? f.semaines.filter(x=>x!==s) : [...f.semaines, s].sort((a,b)=>parseInt(a.slice(1))-parseInt(b.slice(1))) });

  async function saveInfos() {
    setMsg('');
    try {
      if (isNew) { await api.post('/sites', f); onDone(); }
      else { await api.put(`/sites/${site.id}`, f); setMsg('✓ Enregistré'); }
    } catch (e) { setMsg(e.response?.data?.error || 'Erreur'); }
  }
  async function doImport() {
    setMsg('');
    const { data } = await api.post(`/sites/${site.id}/ap/import`, { text: importText });
    setMsg(`✓ ${data.imported} AP importés${data.errors.length ? ` — ${data.errors.length} erreurs` : ''}`);
    setImportText('');
    api.get(`/sites/${site.id}/ap`).then(r => setAps(r.data));
  }
  const apFileRef = { current: null };
  async function importApFile(file) {
    setMsg('');
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post(`/sites/${site.id}/ap/import`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg(`✓ ${data.imported} AP importés depuis le fichier${data.errors.length ? ` — ${data.errors.length} erreurs` : ''}`);
      api.get(`/sites/${site.id}/ap`).then(r => setAps(r.data));
    } catch (e) { setMsg(e.response?.data?.error || 'Import impossible'); }
  }
  const [zones, setZones] = useState([]);
  useEffect(() => {
    if (!isNew && sub === 'ap') api.get(`/sites/${site.id}/zones`).then(r => setZones(r.data)).catch(()=>{});
    // eslint-disable-next-line
  }, [sub]);
  async function renameZone(from) {
    const to = window.prompt(`Renommer la zone « ${from} » en :`, from);
    if (!to || to === from) return;
    const { data } = await api.put(`/sites/${site.id}/zones/rename`, { from, to });
    setMsg(`✓ ${data.updated} AP déplacés vers « ${to} »`);
    api.get(`/sites/${site.id}/zones`).then(r => setZones(r.data));
    api.get(`/sites/${site.id}/ap`).then(r => setAps(r.data));
  }
  async function delAp(id) {
    await api.delete(`/sites/${site.id}/ap/${id}`);
    setAps(a => a.filter(x => x.id !== id));
  }
  async function saveMats() {
    await api.put(`/sites/${site.id}/materiaux`, { lines: mats });
    setMsg('✓ Catalogue enregistré');
  }
  async function saveEquipe() {
    await api.put(`/sites/${site.id}/users`, { userIds: [...assigned] });
    setMsg('✓ Équipe enregistrée');
  }
  async function deleteSite() {
    const typed = window.prompt(
      `Suppression DÉFINITIVE du chantier, de ses AP, rapports, photos, plans et planning.\n` +
      `Pour confirmer, tapez exactement : ${f.nom}`);
    if (typed !== f.nom) { if (typed !== null) setMsg('Nom saisi incorrect — suppression annulée'); return; }
    try { await api.delete(`/sites/${site.id}`); onDone(); }
    catch (e) { setMsg(e.response?.data?.error || 'Suppression impossible'); }
  }

  return (
    <div>
      <button style={st.back} onClick={onDone}>◀ Retour</button>
      <h2 style={{ fontSize:17, fontWeight:800, color:'#1E3A6E', margin:'10px 0' }}>
        {isNew ? 'Nouveau chantier' : f.nom}
      </h2>
      {!isNew && (
        <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' }}>
          {[['infos','Infos'],['ap',`AP (${aps.length})`],['plans','Plans'],['materiaux','Matériaux'],['equipe','Équipe']].map(([t,l]) => (
            <button key={t} style={{...st.chip, ...(sub===t?st.chipOn:{})}} onClick={()=>setSub(t)}>{l}</button>
          ))}
        </div>
      )}
      {msg && <p style={{ color: msg.startsWith('✓') ? '#2E7D32' : '#C62828', marginBottom:10, fontWeight:600 }}>{msg}</p>}

      {(isNew || sub === 'infos') && (
        <div style={st.card}>
          <Field label="Nom du chantier *" value={f.nom} onChange={v=>upd({nom:v})}/>
          <div style={st.grid2}>
            <Field label="Client" value={f.client||''} onChange={v=>upd({client:v})}/>
            <Field label="Ville" value={f.ville||''} onChange={v=>upd({ville:v})}/>
          </div>
          <Field label="Adresse" value={f.adresse||''} onChange={v=>upd({adresse:v})}/>
          <div style={{ margin:'10px 0' }}>
            <p style={st.lbl}>Statut</p>
            <div style={{ display:'flex', gap:6 }}>
              {['ACTIF','TERMINE','ARCHIVE'].map(s => (
                <button key={s} style={{...st.chip, ...(f.statut===s?st.chipOn:{})}} onClick={()=>upd({statut:s})}>{s}</button>
              ))}
            </div>
          </div>
          <div style={{ margin:'10px 0' }}>
            <p style={st.lbl}>Semaines du chantier</p>
            <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
              {SEM_ALL.map(s => (
                <button key={s} style={{...st.chip, padding:'4px 9px', ...(f.semaines.includes(s)?st.chipOn:{})}}
                  onClick={()=>toggleSem(s)}>{s}</button>
              ))}
            </div>
          </div>
          <p style={st.lbl}>Notes / consignes sécurité (affichées au bureau)</p>
          <textarea style={st.ta} rows={2} value={f.notes||''} onChange={e=>upd({notes:e.target.value})}/>
          <div style={{display:'flex', justifyContent:'space-between', marginTop:10, gap:8, flexWrap:'wrap'}}>
            <button style={st.btn} onClick={saveInfos}>Enregistrer</button>
            {!isNew && (
              <button style={{...st.btn, background:'#fff', color:'#C62828', border:'1.5px solid #C62828'}}
                onClick={deleteSite}>Supprimer le chantier</button>
            )}
          </div>
        </div>
      )}

      {!isNew && sub === 'ap' && (
        <div style={st.card}>
          <p style={st.lbl}>Import en masse — une ligne par AP : <b>ref;zone;semaine;type;modele</b> (type : NEW / REUTIL / AVERIFIER)</p>
          <textarea style={{...st.ta, fontFamily:'monospace', fontSize:12}} rows={6} value={importText}
            onChange={e=>setImportText(e.target.value)}
            placeholder={'APA-P001;Bureaux Administratifs;S1;NEW;9130AXI\nAPA-P002;Bureaux Administratifs;S1;REUTIL;9130AXI'}/>
          <div style={{display:'flex', gap:8, marginTop:8, flexWrap:'wrap'}}>
            <button style={st.btn} onClick={doImport} disabled={!importText.trim()}>Importer le texte</button>
            <input type="file" accept=".xlsx,.csv" style={{display:'none'}}
              ref={el => { apFileRef.current = el; }}
              onChange={e => e.target.files[0] && importApFile(e.target.files[0])}/>
            <button style={{...st.btn, background:'#2E75B6'}}
              onClick={() => apFileRef.current?.click()}>Importer un fichier (.xlsx / .csv)</button>
          </div>
          {zones.length > 0 && (
            <div style={{marginTop:14}}>
              <p style={st.lbl}>Zones du chantier (cliquer pour renommer — s'applique à tous les AP de la zone)</p>
              <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                {zones.map(z => (
                  <button key={z.zone} style={st.chip} onClick={() => renameZone(z.zone)}>
                    {z.zone} ({z.count})
                  </button>
                ))}
              </div>
            </div>
          )}
          <div style={{ marginTop:14, maxHeight:400, overflowY:'auto' }}>
            {aps.map(a => (
              <div key={a.id} style={st.apRow}>
                <span style={{ fontWeight:600, minWidth:90 }}>{a.ref}</span>
                <span style={{ flex:1, fontSize:12, color:'#666' }}>{a.zone} · {a.semaine} · {a.type} · {a.modele}</span>
                <button style={st.del} onClick={()=>delAp(a.id)}>✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isNew && sub === 'plans' && <PlansEditor siteId={site.id} aps={aps} setMsg={setMsg}/>}

      {!isNew && sub === 'materiaux' && (
        <div style={st.card}>
          <p style={st.lbl}>Catalogue matériaux proposé au terrain pour ce chantier</p>
          {mats.map((m,i) => (
            <div key={i} style={{ display:'flex', gap:6, marginBottom:5 }}>
              <input style={{...st.inp, flex:3}} value={m.designation}
                onChange={e=>setMats(a=>a.map((x,j)=>j===i?{...x,designation:e.target.value}:x))}/>
              <input style={{...st.inp, flex:0, width:60, textAlign:'center'}} value={m.unite||''} placeholder="u"
                onChange={e=>setMats(a=>a.map((x,j)=>j===i?{...x,unite:e.target.value}:x))}/>
              <button style={st.del} onClick={()=>setMats(a=>a.filter((_,j)=>j!==i))}>✕</button>
            </div>
          ))}
          <div style={{ display:'flex', gap:8, marginTop:8 }}>
            <button style={{...st.btn, background:'#2E75B6'}} onClick={()=>setMats(a=>[...a,{designation:'',unite:''}])}>+ Ligne</button>
            <button style={st.btn} onClick={saveMats}>Enregistrer</button>
          </div>
        </div>
      )}

      {!isNew && sub === 'equipe' && (
        <div style={st.card}>
          <p style={st.lbl}>Utilisateurs affectés à ce chantier</p>
          {users.map(u => (
            <label key={u.id} style={st.userRow}>
              <input type="checkbox" checked={assigned.has(u.id)}
                onChange={() => setAssigned(s => { const n = new Set(s); n.has(u.id) ? n.delete(u.id) : n.add(u.id); return n; })}/>
              <span style={{ fontWeight:600 }}>{u.name}</span>
              <span style={{ fontSize:12, color:'#888' }}>{u.login} · {u.role}</span>
            </label>
          ))}
          <button style={{...st.btn, marginTop:10}} onClick={saveEquipe}>Enregistrer</button>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── PLANS ──────────────────────────
function PlansEditor({ siteId, aps, setMsg }) {
  const [plans, setPlans] = useState([]);
  const [sel, setSel] = useState(null);          // plan sélectionné
  const [status, setStatus] = useState([]);      // AP positionnés + états
  const [placing, setPlacing] = useState(null);  // AP en cours de placement
  const [nom, setNom] = useState('');
  const fileRef = useState(null)[0] || { current: null };
  const inputRef = { current: null };

  const loadPlans = () => api.get(`/sites/${siteId}/plans`).then(r => setPlans(r.data));
  const loadStatus = (planId) => api.get(`/sites/${siteId}/plans/${planId}/status`).then(r => setStatus(r.data));
  useEffect(() => { loadPlans(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (sel) loadStatus(sel.id); /* eslint-disable-next-line */ }, [sel?.id]);

  async function upload(file) {
    const fd = new FormData();
    fd.append('image', file);
    fd.append('nom', nom || file.name.replace(/\.[^.]+$/, ''));
    try {
      await api.post(`/sites/${siteId}/plans`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNom(''); loadPlans(); setMsg('✓ Plan ajouté');
    } catch (e) { setMsg(e.response?.data?.error || 'Erreur upload plan'); }
  }
  async function removePlan(p) {
    if (!window.confirm(`Supprimer le plan « ${p.nom} » ? Les positions des AP seront perdues.`)) return;
    await api.delete(`/sites/${siteId}/plans/${p.id}`);
    if (sel?.id === p.id) setSel(null);
    loadPlans();
  }
  async function place(x, y) {
    if (!placing) return;
    await api.put(`/sites/${siteId}/ap/${placing.id}/position`, { planId: sel.id, posX: x, posY: y });
    setPlacing(null); loadStatus(sel.id);
  }
  async function movePin(ap, x, y) {
    await api.put(`/sites/${siteId}/ap/${ap.id}/position`, { planId: sel.id, posX: x, posY: y });
    loadStatus(sel.id);
  }
  async function unplace(apId) {
    await api.put(`/sites/${siteId}/ap/${apId}/position`, { planId: null });
    loadStatus(sel.id);
  }

  const placedIds = new Set(status.map(a => a.id));
  const unplaced = aps.filter(a => !placedIds.has(a.id));

  return (
    <div>
      <div style={st.card}>
        <p style={st.lbl}>Ajouter un plan (image JPEG/PNG — un par bâtiment, étage ou zone)</p>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <input style={{...st.inp, flex:1, minWidth:180}} placeholder="Nom du plan (ex: RDC — Entrepôt SEC)"
            value={nom} onChange={e=>setNom(e.target.value)}/>
          <input type="file" accept="image/*" style={{display:'none'}}
            ref={el => { inputRef.current = el; }}
            onChange={e => e.target.files[0] && upload(e.target.files[0])}/>
          <button style={st.btn} onClick={() => inputRef.current?.click()}>Choisir l'image</button>
        </div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:12 }}>
          {plans.map(p => (
            <div key={p.id} style={{ display:'flex', alignItems:'center', gap:4 }}>
              <button style={{...st.chip, ...(sel?.id===p.id?st.chipOn:{})}}
                onClick={() => setSel(p)}>{p.nom} ({p._count.accessPoints})</button>
              <button style={st.del} onClick={() => removePlan(p)}>✕</button>
            </div>
          ))}
          {plans.length === 0 && <p style={{fontSize:13,color:'#5A6478'}}>Aucun plan pour l'instant.</p>}
        </div>
      </div>

      {sel && (
        <div className="lw-cols">
          <div style={st.card}>
            <p style={{ fontWeight:700, color:'#13264D', marginBottom:8 }}>
              {sel.nom}
              {placing && <span style={{ color:'#EF6C00', fontWeight:600 }}> — cliquer sur le plan pour placer {placing.ref}</span>}
            </p>
            <PlanView imagePath={sel.imagePath} aps={status} editable
              onPlace={placing ? place : null} onMovePin={movePin}/>
            <p style={{ fontSize:12, color:'#5A6478', marginTop:6 }}>
              Glisser un pin pour le déplacer · cliquer un pin pour voir l'état
            </p>
          </div>
          <div style={st.card}>
            <p style={st.lbl}>AP à placer ({unplaced.length})</p>
            <div style={{ maxHeight:420, overflowY:'auto' }}>
              {unplaced.map(a => (
                <button key={a.id}
                  style={{...st.chip, display:'block', width:'100%', textAlign:'left', marginBottom:4,
                    ...(placing?.id===a.id?st.chipOn:{})}}
                  onClick={() => setPlacing(placing?.id === a.id ? null : a)}>
                  {a.ref} <span style={{opacity:.6}}>· {a.zone}</span>
                </button>
              ))}
              {unplaced.length === 0 && <p style={{fontSize:13,color:'#2E7D32'}}>Tous les AP sont placés.</p>}
            </div>
            {status.length > 0 && (
              <>
                <p style={{...st.lbl, marginTop:12}}>Placés sur ce plan ({status.length})</p>
                <div style={{ maxHeight:200, overflowY:'auto' }}>
                  {status.map(a => (
                    <div key={a.id} style={{ display:'flex', justifyContent:'space-between',
                      alignItems:'center', padding:'3px 0', fontSize:13 }}>
                      <span>{a.ref}</span>
                      <button style={st.del} onClick={() => unplace(a.id)}>retirer</button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── UTILISATEURS ──────────────────────────
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [form, setForm] = useState(null);   // {name, login, role, siteIds}
  const [pwShown, setPwShown] = useState(null); // {login, password}
  const [msg, setMsg] = useState('');
  const load = () => api.get('/users').then(r => setUsers(r.data));
  useEffect(() => { load(); api.get('/sites').then(r => setSites(r.data)); }, []);

  async function create() {
    setMsg('');
    try {
      const { data } = await api.post('/users', form);
      setPwShown({ login: data.user.login, password: data.password });
      setForm(null); load();
    } catch (e) { setMsg(e.response?.data?.error || 'Erreur'); }
  }
  async function toggleActive(u) {
    await api.put(`/users/${u.id}`, { active: !u.active }); load();
  }
  async function resetPw(u) {
    const { data } = await api.post(`/users/${u.id}/reset-password`);
    setPwShown({ login: u.login, password: data.password });
  }
  async function removeUser(u) {
    if (!window.confirm(`Supprimer définitivement le compte « ${u.login} » ?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); }
    catch (e) { alert(e.response?.data?.error || 'Suppression impossible'); }
  }

  return (
    <div>
      {pwShown && (
        <div style={{...st.card, background:'#fff9f0', border:'1.5px solid #EF6C00'}}>
          <p style={{ fontWeight:700, color:'#EF6C00' }}>Mot de passe de « {pwShown.login} » — affiché une seule fois :</p>
          <p style={{ fontFamily:'monospace', fontSize:17, fontWeight:700, margin:'8px 0' }}>{pwShown.password}</p>
          <button style={{...st.chip}} onClick={()=>setPwShown(null)}>J'ai noté, fermer</button>
        </div>
      )}
      {form ? (
        <div style={st.card}>
          <h3 style={{ fontWeight:700, color:'#1E3A6E', marginBottom:10 }}>Nouvel utilisateur</h3>
          {msg && <p style={{ color:'#C62828', marginBottom:8 }}>{msg}</p>}
          <div style={st.grid2}>
            <Field label="Nom complet *" value={form.name} onChange={v=>setForm(f=>({...f,name:v}))}/>
            <Field label="Identifiant *" value={form.login} onChange={v=>setForm(f=>({...f,login:v.toLowerCase()}))}/>
          </div>
          <Field label="Email (requis pour la synthèse hebdo des comptes CLIENT)"
            value={form.email||''} onChange={v=>setForm(f=>({...f,email:v}))}/>
          <p style={st.lbl}>Rôle</p>
          <div style={{ display:'flex', gap:6, marginBottom:10 }}>
            {['TECH','CHEF','BUREAU','ADMIN','CLIENT'].map(r => (
              <button key={r} style={{...st.chip, ...(form.role===r?st.chipOn:{})}}
                onClick={()=>setForm(f=>({...f,role:r}))}>{r}</button>
            ))}
          </div>
          <p style={st.lbl}>Chantiers affectés</p>
          {sites.map(s => (
            <label key={s.id} style={st.userRow}>
              <input type="checkbox" checked={form.siteIds.includes(s.id)}
                onChange={()=>setForm(f=>({...f, siteIds: f.siteIds.includes(s.id)
                  ? f.siteIds.filter(x=>x!==s.id) : [...f.siteIds, s.id]}))}/>
              <span>{s.nom}</span>
            </label>
          ))}
          <div style={{ display:'flex', gap:8, marginTop:10 }}>
            <button style={{...st.btn, background:'#eee', color:'#333'}} onClick={()=>setForm(null)}>Annuler</button>
            <button style={st.btn} onClick={create} disabled={!form.name || !form.login}>Créer</button>
          </div>
        </div>
      ) : (
        <button style={{...st.btn, marginBottom:14}}
          onClick={()=>setForm({ name:'', login:'', role:'TECH', siteIds:[], email:'' })}>+ Nouvel utilisateur</button>
      )}
      {users.map(u => (
        <div key={u.id} style={{...st.rowCard, cursor:'default', opacity: u.active?1:.5}}>
          <div>
            <span style={{ fontWeight:700 }}>{u.name}</span>
            <span style={{ fontSize:12, color:'#888', marginLeft:8 }}>{u.login} · {u.role}</span>
            <p style={{ fontSize:12, color:'#2E75B6' }}>{u.sites.map(s=>s.nom).join(', ') || 'Aucun chantier'}</p>
          </div>
          <div style={{ display:'flex', gap:6 }}>
            <button style={st.chip} onClick={()=>resetPw(u)}>Reset mdp</button>
            <button style={{...st.chip, color:u.active?'#C62828':'#2E7D32'}} onClick={()=>toggleActive(u)}>
              {u.active ? 'Désactiver' : 'Réactiver'}
            </button>
            <button style={{...st.chip, color:'#C62828', borderColor:'#fcc'}}
              onClick={()=>removeUser(u)}>Supprimer</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div style={{ marginBottom:8 }}>
      <p style={st.lbl}>{label}</p>
      <input style={st.inp} value={value} onChange={e=>onChange(e.target.value)}/>
    </div>
  );
}

const st = {
  page:{ minHeight:'100vh' },
  header:{ color:'#fff', padding:'18px 24px',
           display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 },
  title:{ fontSize:19, fontWeight:800, margin:0 },
  sub:{ fontSize:13, opacity:.8, marginTop:2 },
  hBtn:{ background:'rgba(255,255,255,.2)', border:'none', color:'#fff',
         padding:'8px 14px', borderRadius:8, cursor:'pointer', fontSize:13 },
  tabs:{ background:'#fff', borderBottom:'1px solid #DDE4F0' },
  tab:{ padding:'13px 20px', border:'none', background:'none', fontSize:14, cursor:'pointer',
        color:'#666', fontWeight:500, borderBottom:'3px solid transparent' },
  tabOn:{ color:'#13264D', borderBottomColor:'#FFC91F', fontWeight:800 },
  card:{ background:'#fff', borderRadius:12, padding:16, marginBottom:12, boxShadow:'0 1px 4px rgba(0,0,0,.06)' },
  rowCard:{ background:'#fff', borderRadius:10, padding:'13px 16px', marginBottom:8,
            boxShadow:'0 1px 3px rgba(0,0,0,.06)', cursor:'pointer', display:'flex',
            justifyContent:'space-between', alignItems:'center', gap:10, borderLeft:'3px solid #2E75B6' },
  badge:{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20 },
  btn:{ background:'#1E3A6E', color:'#fff', borderRadius:10, padding:'10px 18px',
        fontSize:14, fontWeight:600, border:'none', cursor:'pointer' },
  back:{ background:'none', border:'none', color:'#2E75B6', fontSize:14, fontWeight:600, cursor:'pointer', padding:0 },
  chip:{ border:'1.5px solid #ccc', borderRadius:20, padding:'5px 12px', fontSize:12,
         background:'#fff', color:'#555', fontWeight:500, cursor:'pointer' },
  chipOn:{ background:'#1E3A6E', color:'#fff', borderColor:'#1E3A6E' },
  lbl:{ fontSize:12, color:'#666', marginBottom:4, fontWeight:600 },
  inp:{ width:'100%', border:'1.5px solid #e0e0e0', borderRadius:8, padding:'9px 11px', fontSize:14 },
  ta:{ width:'100%', border:'1.5px solid #e0e0e0', borderRadius:8, padding:'9px 11px', fontSize:13, fontFamily:'inherit' },
  grid2:{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 },
  apRow:{ display:'flex', alignItems:'center', gap:8, padding:'6px 8px', borderBottom:'1px solid #f0f0f0' },
  del:{ background:'#fee', color:'#C62828', border:'1px solid #fcc', borderRadius:6,
        padding:'3px 8px', fontSize:12, cursor:'pointer' },
  userRow:{ display:'flex', alignItems:'center', gap:10, padding:'7px 4px', cursor:'pointer' },
};
